
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/lib/types/auth";
import { UserTableView } from "@/components/admin/users/UserTableView";
import { useUserManagementService } from "@/components/admin/users/UserManagementService";
import { Skeleton } from "@/components/ui/skeleton";

export default function ManageUsers() {
  const { handleRoleChange, handleActiveStatusChange, handleDeleteUser } = useUserManagementService();
  
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      // Get profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // For each profile, we would ideally fetch the email separately
      // Since we can't directly join with auth.users table
      // Instead, we'll return profiles without emails for now
      const profilesWithEmail = data.map(profile => ({
        ...profile,
        email: null // We'll display a placeholder in the UI
      }));
      
      return profilesWithEmail as (Profile & { email: string | null })[];
    },
  });

  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-7xl animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-title font-bold tracking-tight text-primary">Manage Users</h1>
          <p className="text-muted-foreground font-subtitle">View and manage user accounts</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : users && (
        <UserTableView 
          users={users}
          onRoleChange={handleRoleChange}
          onStatusChange={handleActiveStatusChange}
          onDeleteUser={handleDeleteUser}
        />
      )}
    </div>
  );
}
